import React from 'react';

/**
 * MakeItExist — § II of the new product-led homepage.
 *
 * "Just make it exist first."  This section is the proof:
 * three macro photographs of the physical reality being made in
 * Heavener, Oklahoma — long before the brand says a single word
 * about it.
 *
 * Three stills:
 *   I.   the kewra flower (the input)
 *   II.  the Miron-violet vessel with its first droplet (the witness)
 *   III. linen-wrapped bottle leaving the atelier (the artefact)
 */

const STILLS = [
  {
    id:     'kewra',
    src:    '/atelier/kewra-flower.png',
    no:     'I',
    title:  'The Bloom',
    note:   'Pandanus (Kewra) · the single botanical witnessed',
  },
  {
    id:     'vessel',
    src:    '/atelier/miron-droplet.png',
    no:     'II',
    title:  'The Vessel',
    note:   '251 ml Miron Glass · cold-fill · no air, no light, no compromise',
  },
  {
    id:     'linen',
    src:    '/atelier/linen-atelier.png',
    no:     'III',
    title:  'The Hand',
    note:   'Linen-wrapped · hand-finished · Heavener, Oklahoma',
  },
];

const MakeItExist = ({ testId = 'home-make-it-exist' }) => (
  <section
    data-testid={testId}
    className="relative w-full"
    style={{ backgroundColor: '#0E1A22' }}
  >
    {/* Gold top hairline */}
    <span
      aria-hidden
      className="block w-full h-px"
      style={{
        background:
          'linear-gradient(90deg, transparent, rgba(212,175,90,0.55) 40%, rgba(212,175,90,0.55) 60%, transparent)',
      }}
    />

    <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
      {/* Header */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-16 md:mb-20">
        <div className="md:col-span-7">
          <p
            data-testid={`${testId}-overline`}
            className="font-mono text-[10px] md:text-[12px] tracking-[0.4em] uppercase font-semibold text-[#D4AF5A] mb-6 flex items-center gap-3"
          >
            <span aria-hidden className="inline-block w-10 h-px bg-[#D4AF5A]" />
            Just make it exist first
          </p>
          <h2
            data-testid={`${testId}-title`}
            className="font-serif font-light text-[#F4F8F9] leading-[1.02]"
            style={{ fontSize: 'clamp(36px, 5vw, 76px)' }}
          >
            The ritual exists
            <br />
            in <em className="text-[#D4AF5A]">Heavener</em>,
            <br />
            long before it
            <br />
            exists as a brand.
          </h2>
        </div>
        <div className="md:col-span-5 md:pt-4">
          <p
            data-testid={`${testId}-thesis`}
            className="font-serif italic text-lg md:text-xl text-[#F4F8F9]/85 leading-relaxed max-w-md"
          >
            Three things, made by hand. They exist whether or not
            anyone sees them. The website is the receipt; the bottle
            is the truth.
          </p>
        </div>
      </div>

      {/* Three-up macro grid */}
      <ol
        data-testid={`${testId}-grid`}
        className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
      >
        {STILLS.map((s) => (
          <li
            key={s.id}
            data-testid={`${testId}-still-${s.id}`}
            className="relative group overflow-hidden border border-[#F4F8F9]/15 bg-[#0E1A22]"
          >
            <figure className="relative">
              <img
                src={s.src}
                alt={s.note}
                loading="lazy"
                className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-[1.02]"
              />
              {/* Top-left numeral */}
              <span
                aria-hidden
                className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.34em] uppercase text-[#D4AF5A] font-semibold bg-[#0E1A22]/65 backdrop-blur-md px-2.5 py-1"
              >
                {s.no}
              </span>
              <figcaption className="absolute left-0 right-0 bottom-0 px-5 py-4 backdrop-blur-md bg-[#0E1A22]/72">
                <p
                  className="font-serif text-xl md:text-2xl text-[#F4F8F9] leading-tight"
                >
                  {s.title}
                </p>
                <p className="mt-1 font-mono text-[10px] tracking-[0.26em] uppercase text-[#F4F8F9]/75">
                  {s.note}
                </p>
              </figcaption>
            </figure>
          </li>
        ))}
      </ol>

      <p
        data-testid={`${testId}-coda`}
        className="mt-12 md:mt-16 text-center font-mono text-[10px] md:text-[11px] tracking-[0.36em] uppercase text-[#F4F8F9]/55"
      >
        Made first · explained second · marketed last
      </p>
    </div>

    {/* Gold bottom hairline */}
    <span
      aria-hidden
      className="block w-full h-px"
      style={{
        background:
          'linear-gradient(90deg, transparent, rgba(212,175,90,0.55) 40%, rgba(212,175,90,0.55) 60%, transparent)',
      }}
    />
  </section>
);

export default MakeItExist;
